import React, { forwardRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { useDelayUnmount } from 'hooks';
import { DialogOverlay, DialogContent } from '@reach/dialog';
import '@reach/dialog/styles.css';

const Dialog = forwardRef((props, ref) => {
  const dialogAnimationTime = 300;
  const {
    isVisible,
    children,
    onDismiss,
    overlayStyle,
    contentStyle,
    keepMounted = true,
    _key
  } = props;

  // Always render the content for SEO, but in a hidden container when dialog is closed
  const hiddenContent = (
    <div
      key={`hidden-${_key}`}
      style={{
        position: 'absolute',
        left: '-9999px',
        top: 0,
        height: 0,
        overflow: 'hidden',
        visibility: 'hidden',
        pointerEvents: 'none'
      }}
      aria-hidden="true">
      {children}
    </div>
  );

  const shouldRenderChild = useDelayUnmount(isVisible, dialogAnimationTime);

  // Only render visible dialog if it's open
  const visibleDialog = (isVisible || (!isVisible && shouldRenderChild)) && (
    <StyledDialogOverlay
      ref={ref}
      key={`dialog-${_key}`}
      isOpen
      onDismiss={onDismiss}
      $overlayStyle={overlayStyle}
      $dialogAnimationTime={dialogAnimationTime}>
      <StyledDialogContent $contentStyle={contentStyle} $dialogAnimationTime={dialogAnimationTime}>
        {children}
      </StyledDialogContent>
    </StyledDialogOverlay>
  );

  return (
    <>
      {visibleDialog}
      {keepMounted && !isVisible && hiddenContent}
    </>
  );
});

const fadeIn = keyframes`
  0% {
    opacity: 0;
  }

  100% {
    opacity: 1;
  }
`;

const fadeOut = keyframes`
  0% {
    opacity: 1;;
  }

  100% {
    opacity: 0;;
  }
`;

const slideUp = keyframes`
  0% {
    transform: translateY(10%);
  }

  100% {
    transform: translateY(0);
  }
`;

const slideDown = keyframes`
  0% {
    transform: translate(0);
  }

  100% {
    transform: translateY(10%);
  }
`;

const StyledDialogOverlay = styled(DialogOverlay)`
  align-items: center;
  backdrop-filter: blur(0.125rem);
  background-color: rgb(0 0 0 / 25%);
  display: flex;
  justify-content: center;
  overflow-x: hidden !important;
  padding: 0.75rem;
  transform-style: preserve-3d;
  z-index: 7;
  ${({ $overlayStyle }) => $overlayStyle};

  &[data-state='open'] {
    animation: ${fadeIn} ${({ $dialogAnimationTime }) => `${$dialogAnimationTime}ms`} ease-in-out
      forwards;
  }

  &[data-state='close'],
  &[data-state='dismiss'] {
    animation: ${fadeOut} ${({ $dialogAnimationTime }) => `${$dialogAnimationTime}ms`} ease-in-out
      forwards;
  }
`;

const StyledDialogContent = styled(DialogContent)`
  background-color: transparent !important;
  box-sizing: border-box;
  padding: 0 !important;
  transform-style: preserve-3d;
  width: 100% !important;
  ${({ $contentStyle }) => $contentStyle};

  &[data-state='open'] {
    animation: ${slideUp} ${({ $dialogAnimationTime }) => `${$dialogAnimationTime}ms`} ease-in-out
      forwards;
  }

  &[data-state='dismiss'] {
    animation: ${slideDown} ${({ $dialogAnimationTime }) => `${$dialogAnimationTime}ms`} ease-in-out
      forwards;
  }
`;

export default Dialog;
